import {EditPlannedAccessFormValidationSchema} from '@/features/planned-accesses/formSchemas'
import {ProjectPlannedAccess} from '@/features/planned-accesses/types'
import {raise} from '@/utilities/helpers'
import {zodResolver} from '@hookform/resolvers/zod'
import {ReactNode} from 'react'
import {FormProvider, useForm} from 'react-hook-form'

const remapProjectPlannedAccessesToDefaultValues = (
    defaultValues: ProjectPlannedAccess,
    isMixedProject: boolean
): EditPlannedAccessFormValidationSchema => {
    return {
        id: defaultValues.id,
        projectId: defaultValues.projectId,
        isMixedProject,
        inPersonAccesses: defaultValues.plannedAccesses
            .filter(access => access.accessType.groupName === 'treatments')
            .map(access => ({
                accessType: access.accessType,
                numberOfAccesses: access.numberOfAccesses,
                frequency: access.frequency ?? 'weekly',
                sector: isMixedProject ? access.sector ?? raise('sector is required for mixed projects') : undefined
            })),
        singleAccesses: defaultValues.plannedAccesses
            .filter(access => access.accessType.groupName === 'in_person_accesses')
            .map(access => ({
                accessType: access.accessType,
                numberOfAccesses: access.numberOfAccesses
            })),
        absenceAccesses: defaultValues.plannedAccesses
            .filter(absenceAccess =>
                absenceAccess.accessType?.accessType
                    ? absenceAccess.accessType.groupName == 'non_in_person_accesses'
                    : false
            )
            .map(access => ({
                accessType: access.accessType,
                numberOfAccesses: access.numberOfAccesses
            })),
        treatmentsPlan: defaultValues.treatmentPlan ?? '',
        objectives: defaultValues.targets ?? '',
        additionalNotes: defaultValues.additionalNotes ?? ''
    }
}

export const EditPlannedAccessesFormProvider = ({
    children,
    defaultValues,
    isMixedProject
}: {
    children: ReactNode
    defaultValues: ProjectPlannedAccess
    isMixedProject: boolean
}) => {
    const form = useForm<EditPlannedAccessFormValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(EditPlannedAccessFormValidationSchema),
        defaultValues: remapProjectPlannedAccessesToDefaultValues(defaultValues, isMixedProject)
    })

    return <FormProvider {...form}>{children}</FormProvider>
}
